import React from 'react';
import { AlertTriangle, Database, Flag, Settings2, Terminal } from 'lucide-react';
import type { RuntimeFlag } from '../../generated/api/types.gen.js';
import { Badge, Button, Callout, DataTable, EmptyState, Toggle } from '../../components/ui/index.js';
import type { BadgeVariant, DataTableColumn } from '../../components/ui/index.js';
import { isToggleLocked } from './runtime-flag-rules.js';

const RISK_VARIANT: Record<RuntimeFlag['risk'], BadgeVariant> = {
  safe: 'success',
  caution: 'warning',
  dangerous: 'danger',
};

const RISK_LABEL: Record<RuntimeFlag['risk'], string> = {
  safe: 'Safe',
  caution: 'Caution',
  dangerous: 'Dangerous',
};

const APPLIES_AT_COPY: Record<RuntimeFlag['appliesAt'], string> = {
  immediate: 'Applies immediately',
  'next-tick': 'Applies on the next scheduled tick',
  restart: 'Requires a process restart',
};

const SOURCE_COPY: Record<RuntimeFlag['source'], string> = {
  db: 'Set by an admin',
  env: 'Inherited from an environment variable',
  default: 'Registry default, never explicitly set',
};

const SOURCE_ICON = { db: Database, env: Terminal, default: Settings2 } as const;

interface RuntimeFlagsTableProps {
  title: string;
  description: string;
  flags: RuntimeFlag[];
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  canWrite: boolean;
  pendingKey: string | null;
  onToggle: (flag: RuntimeFlag, value: boolean) => void;
}

/** One scope of runtime flags (platform or organization) as a titled table with a switch per row. */
export const RuntimeFlagsTable: React.FC<RuntimeFlagsTableProps> = ({
  title,
  description,
  flags,
  loading,
  failed,
  onRetry,
  canWrite,
  pendingKey,
  onToggle,
}) => {
  const columns: DataTableColumn<RuntimeFlag>[] = [
    {
      key: 'flag',
      header: 'Flag',
      render: (flag) => (
        <div className="min-w-0 max-w-xl">
          <span className="block text-sm font-medium text-heading">{flag.label}</span>
          <code className="block text-xs text-muted">{flag.key}</code>
          <span className="mt-1 block text-xs text-muted">{flag.description}</span>
        </div>
      ),
    },
    {
      key: 'risk',
      header: 'Risk',
      render: (flag) => <Badge variant={RISK_VARIANT[flag.risk]}>{RISK_LABEL[flag.risk]}</Badge>,
    },
    {
      key: 'appliesAt',
      header: 'Applies',
      render: (flag) => <span className="text-sm text-body">{APPLIES_AT_COPY[flag.appliesAt]}</span>,
    },
    {
      key: 'source',
      header: 'Value from',
      render: (flag) => {
        const Icon = SOURCE_ICON[flag.source];
        return (
          <div className="text-xs text-muted">
            <span className="inline-flex items-center gap-1.5">
              <Icon size={14} aria-hidden="true" />
              {SOURCE_COPY[flag.source]}
            </span>
            {/* Named even when the database wins, so an admin can see which variable is being overridden. */}
            {flag.envVar && <span className="block">Environment fallback: {flag.envVar}</span>}
          </div>
        );
      },
    },
    {
      key: 'value',
      header: 'Value',
      align: 'right',
      render: (flag) => (
        <Toggle
          checked={flag.value}
          disabled={isToggleLocked({ canWrite, pending: pendingKey === flag.key })}
          onChange={(next) => onToggle(flag, next)}
          label={flag.value ? 'On' : 'Off'}
        />
      ),
    },
  ];

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-heading">{title}</h2>
        <p className="text-sm text-muted">{description}</p>
      </div>
      {failed ? (
        <Callout
          tone="danger"
          icon={AlertTriangle}
          title="Could not load these flags"
          actions={
            <Button size="sm" variant="secondary" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Check your connection and try again.
        </Callout>
      ) : (
        <DataTable
          framed
          columns={columns}
          rows={flags}
          rowKey={(flag) => flag.key}
          loading={loading}
          aria-label={`${title} flags`}
          empty={
            <EmptyState
              icon={Flag}
              title="No flags registered"
              description="Nothing is registered in this scope yet."
            />
          }
        />
      )}
    </section>
  );
};
